import LazyBrush from "@/lib/LazyBrush.ts";
import { RemoveScroll } from "@mantine/core";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

type CanvasProps = {
  brushSize: number;
  isErasing: boolean;
  width: number;
  height: number;
  setMask: (mask: Blob) => void;
};

export type CanvasHandles = {
  handleClear: () => void;
};

export const Canvas = forwardRef<CanvasHandles, CanvasProps>((props, ref) => {
  const { brushSize, isErasing, width, height, setMask } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isTapping, setIsTapping] = useState(false);

  const lazy = new LazyBrush({
    enabled: true,
    radius: brushSize,
  });

  const handleMouseDown = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const x = e.offsetX;
    const y = e.offsetY;
    lazy.update({ x, y });

    const brushHasMoved = lazy.brushHasMoved();

    if (brushHasMoved) {
      const brush = lazy.getBrushCoordinates();
      ctx.beginPath();
      ctx.fillStyle = isErasing ? "black" : "white";
      ctx.arc(brush.x, brush.y, brushSize, 0, Math.PI * 2, true);
      ctx.fill();
    }

    handleSave();
  };

  function getTouchPos(canvas: HTMLCanvasElement, touch: Touch) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width; // relationship bitmap vs. element for X
    const scaleY = canvas.height / rect.height; // relationship bitmap vs. element for Y

    return {
      x: (touch.clientX - rect.left) * scaleX, // scale mouse coordinates after they have
      y: (touch.clientY - rect.top) * scaleY, // been adjusted to be relative to element
    };
  }

  const handleTouchMove = (e: TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    lazy.update(getTouchPos(canvas, e.touches[0]));

    const brushHasMoved = lazy.brushHasMoved();

    if (brushHasMoved) {
      const brush = lazy.getBrushCoordinates();
      ctx.beginPath();
      ctx.fillStyle = isErasing ? "black" : "white";
      ctx.arc(brush.x, brush.y, brushSize, 0, Math.PI * 2, true);
      ctx.fill();
    }

    handleSave();
  };

  const handleMouseUp = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.removeEventListener("mousemove", handleMouseMove);
    canvas.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchEnd = () => {
    setIsTapping(false);
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.removeEventListener("touchmove", handleTouchMove);
    canvas.removeEventListener("touchend", handleTouchEnd);
  };

  const handleTouchStart = () => {
    setIsTapping(true);
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    canvas.addEventListener("touchmove", handleTouchMove);
    canvas.addEventListener("touchend", handleTouchEnd);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    handleSave();
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      setMask(blob);
    }, "image/png");
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    setTimeout(() => {
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      handleSave();
    }, 100);
  }, [canvasRef.current]);

  useImperativeHandle(ref, () => ({
    handleClear,
  }));

  return (
    <RemoveScroll enabled={isTapping}>
      <div className="opacity-20 cursor-crosshair">
        <canvas
          ref={canvasRef}
          className="rounded-lg"
          width={width}
          height={height}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      </div>
    </RemoveScroll>
  );
});
